import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import Button from '../../ui-core/Button/Button';
import FiltersSkeletons from '../../ui-core/Skeletons/FiltersSkeletons';
import { fitnessOptionsGetRequest } from '../../api/fitness';
import {
  extractApiError,
  getCommunityProfileExtendedRequest,
} from '../../api/community';
import { updateUserProfileRequest } from '../../api/user';
import { LOCAL_STORAGE_CONSTANT } from '../../constants/app';
import {
  createNotification,
  NOTIFICATION_TYPE,
  SUCCESS_MESSAGES,
} from '../../services/Notification';
import { extractJSON } from '../../services/jsonUtil';
import { setItem } from '../../utils/cache';
import { errorHandler, scrollToTop } from '../../utils/helpers';
import './style.css';

const getPartId = part => {
  const id = part?.value ?? part?.id ?? null;
  return id == null || id === '' ? null : id;
};

const partIdsMatch = (a, b) =>
  a != null && b != null && String(a) === String(b);

const partIdsEqual = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const extractExtendedProfile = response => {
  const body = response?.data;
  if (!body) return {};
  if (
    body.primary_body_part !== undefined ||
    body.targeted_body_part !== undefined
  ) {
    return body;
  }
  return body.data ?? {};
};

const buildPointPayload = (partId, partOptions) => {
  if (partId == null) return [];
  const option = partOptions.find(item => partIdsMatch(item.id, partId));
  return [
    {
      id: String(option?.id ?? partId),
      name: option?.attributes?.name ?? '',
    },
  ];
};

const buildUserProfileAttributes = (
  primaryPartId,
  secondaryPartId,
  partOptions,
) => ({
  primary_body_part: primaryPartId != null ? Number(primaryPartId) : null,
  targeted_body_part: secondaryPartId != null ? Number(secondaryPartId) : null,
  primary_point:
    primaryPartId != null
      ? buildPointPayload(primaryPartId, partOptions)
      : null,
  target_point:
    secondaryPartId != null
      ? buildPointPayload(secondaryPartId, partOptions)
      : null,
});

const PainPoints = () => {
  const [activeTab, setActiveTab] = useState('primary');
  const [partOptions, setPartOptions] = useState([]);
  const [primaryPartId, setPrimaryPartId] = useState(null);
  const [secondaryPartId, setSecondaryPartId] = useState(null);
  const [initialPrimaryPartId, setInitialPrimaryPartId] = useState(null);
  const [initialSecondaryPartId, setInitialSecondaryPartId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let options = extractJSON(LOCAL_STORAGE_CONSTANT.BODY_PARTS_OPTIONS);
      if (!options?.length) {
        const partsResponse = await fitnessOptionsGetRequest();
        options = partsResponse?.data?.response?.data ?? [];
        if (options.length) {
          setItem(
            LOCAL_STORAGE_CONSTANT.BODY_PARTS_OPTIONS,
            JSON.stringify(options),
          );
        }
      }
      setPartOptions(options);

      const profileResponse = await getCommunityProfileExtendedRequest();
      const profile = extractExtendedProfile(profileResponse);
      const loadedPrimaryId = getPartId(profile.primary_body_part);
      const loadedSecondaryId = getPartId(profile.targeted_body_part);
      setPrimaryPartId(loadedPrimaryId);
      setSecondaryPartId(loadedSecondaryId);
      setInitialPrimaryPartId(loadedPrimaryId);
      setInitialSecondaryPartId(loadedSecondaryId);
    } catch (err) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: extractApiError(err, 'Failed to load pain points.'),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    scrollToTop();
    loadData();
  }, [loadData]);

  const currentSelectionId =
    activeTab === 'primary' ? primaryPartId : secondaryPartId;

  const setCurrentSelectionId = id => {
    if (activeTab === 'primary') {
      setPrimaryPartId(id);
    } else {
      setSecondaryPartId(id);
    }
  };

  const handlePartToggle = option => {
    const optionId = option.id;
    setCurrentSelectionId(
      partIdsMatch(currentSelectionId, optionId) ? null : optionId,
    );
  };

  const handleClearAll = () => {
    setCurrentSelectionId(null);
  };

  const handleSave = async () => {
    const user = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
    if (!user?.id) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: 'Unable to save. Please sign in again.',
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateUserProfileRequest(user.id, {
        email: user?.attributes?.email,
        name: user?.attributes?.name ?? '',
        user_profile_attributes: buildUserProfileAttributes(
          primaryPartId,
          secondaryPartId,
          partOptions,
        ),
      });
      const userObject = response?.data?.response?.data;
      if (userObject) {
        setItem(LOCAL_STORAGE_CONSTANT.USER, JSON.stringify(userObject));
      }
      setInitialPrimaryPartId(primaryPartId);
      setInitialSecondaryPartId(secondaryPartId);
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: SUCCESS_MESSAGES.USER_PROFILE_UPDATE_SUCCESS,
      });
    } catch (err) {
      createNotification(errorHandler(err));
    } finally {
      setIsSaving(false);
    }
  };

  const heading =
    activeTab === 'primary'
      ? 'Choose your primary pain point?'
      : 'Choose your secondary pain point?';

  const hasChanges =
    !partIdsEqual(primaryPartId, initialPrimaryPartId) ||
    !partIdsEqual(secondaryPartId, initialSecondaryPartId);
  const canSave = hasChanges;

  return (
    <div className="pain-points-page pain-points-profile-section">
      <div className="pain-points-container">
        <h1 className="pain-points-title">Pain points</h1>

        <div className="pain-points-tabs">
          <button
            type="button"
            className={clsx(
              'pain-points-tab',
              activeTab === 'primary' && 'active',
            )}
            onClick={() => setActiveTab('primary')}
          >
            Primary
          </button>
          <button
            type="button"
            className={clsx(
              'pain-points-tab',
              activeTab === 'secondary' && 'active',
            )}
            onClick={() => setActiveTab('secondary')}
          >
            Secondary
          </button>
        </div>

        <div className="pain-points-content">
          <h2 className="pain-points-heading">{heading}</h2>
          <p className="pain-points-subtitle">
            Select your target body part or area:
          </p>

          <div className="pain-points-options">
            {isLoading
              ? Array.from({ length: 14 }).map((_, index) => (
                  <FiltersSkeletons key={`pain-point-skeleton-${index}`} />
                ))
              : partOptions.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    className={clsx(
                      'pain-point-option',
                      partIdsMatch(currentSelectionId, option.id) && 'active',
                    )}
                    onClick={() => handlePartToggle(option)}
                  >
                    {option.attributes?.name}
                  </button>
                ))}
          </div>

          <div className="pain-points-clear">
            <button
              type="button"
              className="clear-all-button"
              onClick={handleClearAll}
              disabled={currentSelectionId == null}
            >
              Clear all
            </button>
          </div>

          <div className="pain-points-save">
            <Button
              text="SAVE"
              class="save-button"
              onClick={handleSave}
              isDisabled={!canSave || isSaving || isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

PainPoints.propTypes = {};

export default PainPoints;
